import { useEffect, useState } from 'react';
import { type BuildRequirementsType } from '@common/types/quarryType';
import { useAppSelector } from '@redux/store';

import { type RequirementsItemProps } from '../types/components';
import { type UnionGameItemType } from '../types/userType';
import { type WorkshopRequirements } from '../types/workshopType';

interface UseCheckRequirementsReturnType {
  isExist: boolean;
  isComplete: boolean;
}

const useCheckPlantsCheck = (
  plants: Partial<Record<UnionGameItemType, number>> | undefined,
): boolean => {
  const plantsData = useAppSelector((state) => state.user.user?.plants);
  const [isPlantsComplete, setIsPlantsComplete] = useState(false);

  useEffect(() => {
    const checkPlants = (): boolean => {
      if (plants) {
        return Object.entries(plants).every(([plant, value]) =>
          plantsData && plant in plantsData
            ? (plantsData[plant as UnionGameItemType]?.value || 0) >= value!
            : false,
        );
      } else {
        return true;
      }
    };

    setIsPlantsComplete(checkPlants());
  }, [plants, plantsData]);

  return isPlantsComplete;
};

const useCheckRequirements = (
  requirements: BuildRequirementsType | WorkshopRequirements | undefined,
): UseCheckRequirementsReturnType => {
  const storage = useAppSelector((state) => state.user.user?.storage);
  const plantsData = useAppSelector((state) => state.user.user?.plants);
  const cashBalance = useAppSelector((state) => state.user.user?.balance.cash);
  const isPlantsComplete = useCheckPlantsCheck(requirements?.plants);
  const [requirementsCheck, setIsRequirementsCheck] =
    useState<UseCheckRequirementsReturnType>({
      isExist: false,
      isComplete: false,
    });

  useEffect(() => {
    if (!requirements) {
      return;
    }

    const checkMaterials = (): boolean => {
      if (requirements.materials) {
        return Object.entries(requirements.materials).every(
          ([material, value]) =>
            storage && material in storage
              ? (storage[material]?.value || 0) >= value
              : false,
        );
      } else {
        return true;
      }
    };

    const cashRequirementIsComplete =
      (cashBalance || 0) >= (requirements?.cash || 0);

    setIsRequirementsCheck({
      isComplete:
        cashRequirementIsComplete && isPlantsComplete && checkMaterials(),
      isExist: true,
    });
  }, [requirements, storage, cashBalance, plantsData, isPlantsComplete]);

  return requirementsCheck;
};

const useCheckRequirementItem = ({
  type,
  resourceName,
  value,
}: RequirementsItemProps): { isComplete: boolean; currentValue: number } => {
  const storage = useAppSelector((state) => state.user.user?.storage);
  const plantsData = useAppSelector((state) => state.user.user?.plants);
  const cashBalance = useAppSelector((state) => state.user.user?.balance.cash);
  const [isComplete, setIsComplete] = useState(false);
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    let result: boolean = false;
    let currentValue: number = 0;

    if (type === 'cash') {
      result = (cashBalance || 0) >= value;
      currentValue = cashBalance || 0;
    }

    if (type === 'material') {
      const storageHasMaterial =
        storage && resourceName && resourceName in storage;
      const materialBalance = storageHasMaterial
        ? storage[resourceName]?.value || 0
        : 0;
      result = materialBalance >= value;
      currentValue = materialBalance;
    }

    if (type === 'plant' || type === 'quarry') {
      const plantHasData =
        plantsData && resourceName && resourceName in plantsData;
      const plantLevel = plantHasData
        ? plantsData[resourceName as keyof typeof plantsData]?.value || 0
        : 0;
      result = plantLevel >= value;
      currentValue = plantLevel;
    }

    setIsComplete(result);
    setCurrentValue(currentValue);
  }, [storage, plantsData, cashBalance, type, resourceName, value]);

  return { isComplete, currentValue };
};

export {
  useCheckPlantsCheck,
  useCheckRequirementItem,
  useCheckRequirements,
  type UseCheckRequirementsReturnType,
};
